import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";

import { Button, Popup } from "devextreme-react";
import { IItemProps } from "devextreme-react/form";

import { getUserProfileFromCache } from "../../services/userSettingService";
import { buildMappingDimensionValuesMap } from "../../services/mappingDimensionService";
import { MappingDimensionValue } from "../../model/model";

import { InactivatableCrudDataGrid } from "../common/InactivatableCrudDataGrid";

import { BaseMappingsProps, MappingModelBase } from "./model/model";
import { ArmMappingDimensions } from "./ArmMappingDimensions";

export const BaseMappings = (pageProps: BaseMappingsProps) => {
  pageProps.logger.debug("Rendering BaseMappings");
  const [selectedMapping, setSelectedMapping] = useState<MappingModelBase>();
  const [popupVisible, setPopupVisible] = useState(false);
  const [popupContentType, setPopupContentType] = useState<string>();
  const [mappingDimensionValuesMap, setMappingDimensionValuesMap] = useState<Map<string, MappingDimensionValue[]>>();
  const organizationId = getUserProfileFromCache(pageProps.dataStore).defaultOrganization;

  useEffect(() => {
    const fetchMappingDimensionValues = async () => {
      try {
        const httpClient = pageProps.appUtil.httpClients["eAccountingService"];
        const valuesMap = await buildMappingDimensionValuesMap(httpClient, organizationId);
        setMappingDimensionValuesMap(valuesMap);
      } catch (error) {
        pageProps.logger.error("Failed to fetch mapping dimension values", error);
      }
    };
    fetchMappingDimensionValues();
  }, [organizationId, pageProps.appUtil.httpClients, pageProps.logger]);

  const onSelectionChanged = useCallback((selectedRowsData : MappingModelBase[]) => {
    const data = selectedRowsData[0];
    setSelectedMapping(data);
  }, []);

  const hasSelection = selectedMapping !== undefined && selectedMapping !== null;

  const mappingDimensionsButton = (
    <Button
      icon="codeblock"
      hint="View Mapping Dimensions"
      disabled={!hasSelection}
      onClick={() => {
        if (!selectedMapping) {
          alert("Please select a mapping first!");
          return;
        }
        setPopupContentType("mappingDimensions");
        setPopupVisible(true);
      }}
    />
  );

  const getPopupContent = () => {
    if(!popupVisible) {
      return <></>;
    }
    if (popupContentType) {
      const popupContent = pageProps.getPopupContent?.(
        popupContentType,
        selectedMapping,
        hidePopup,
      );
      if (popupContent) {
        return popupContent;
      }
    }
    if (popupContentType === "mappingDimensions") {
      return (
        <ArmMappingDimensions
          pageProps={pageProps}
          logger={pageProps.logger}
          organizationId={organizationId}
          mappingData={selectedMapping!}
          onClosing={hidePopup}
          onSaving={onMappingDimensionsSaving}
          mappingDimensionValuesMap={mappingDimensionValuesMap}
        />
      );
    }
    return (
      <div>
        <span>Unkown popup content type!</span>
      </div>
    );
  };

  const hidePopup = useCallback(() => {
    setPopupVisible(false);
  }, [setPopupVisible]);

  const onMappingDimensionsSaving = useCallback(async (mappingData: MappingModelBase) => {
    pageProps.logger.debug("BaseMappings onMappingDimensionsSaving: " + JSON.stringify(mappingData.mappingDimensions));
    // Update the selected mapping with the new mapping dimensions
    if (selectedMapping) {
      selectedMapping.mappingDimensions = mappingData.mappingDimensions;
        const updateUrl = `/${pageProps.baseUrl}/update-fields`;
        const udpateData = {
            key: {
              organizationId: organizationId,
              mappingName: selectedMapping.mappingName,
            },
            updateValues: {
              MappingDimensions: selectedMapping.mappingDimensions,
            },
          };
        const updateResponse = await pageProps.appUtil.httpClients["eAccountingService"].put(
          updateUrl,
          udpateData,
          (err) => {
            throw err;
          },
        );
    }
  }, [selectedMapping, pageProps.logger]);

  let additionalButtons = [mappingDimensionsButton] as ReactNode[];
  if (pageProps.additionalButtons) {
    additionalButtons = additionalButtons.concat(
      pageProps.additionalButtons.map((buttonProps) => {
        return (
          <Button
            icon={buttonProps.icon}
            hint={buttonProps.hint}
            disabled={!hasSelection}
            onClick={() => {
              if (!selectedMapping) {
                alert("Please select a mapping first!");
                return;
              }
              setPopupContentType(buttonProps.contentType);
              setPopupVisible(true);
            }}
          />
        );
      }),
    );
  }

  const dataToBeFilteredForForm = ["mappingDimensions"].concat(pageProps.additionalDataToBeFilteredForForm || []);

  const editorItemOptions = useMemo(() => {
    const options = new Map<string, IItemProps>([
      [
        "active",
        {
          editorType: "dxCheckBox",
        },
      ],
    ]);
    if (pageProps.additionalEditorItemOptions) {
      pageProps.additionalEditorItemOptions.forEach((value, key) => {
        options.set(key, value);
      });
    }
    return options;
  }, [pageProps.additionalEditorItemOptions]);

  const beforeSave = async (data: any) => {
    (data as MappingModelBase).mappingDimensions = [];
    await pageProps.beforeSave?.(data);
    return data;
  }

  return (
    <>
      <InactivatableCrudDataGrid
        pageProps={pageProps}
        logger={pageProps.logger}
        dummyRow={pageProps.dummyRow}
        baseUrl={pageProps.baseUrl}
        isOrganizationData={true}
        keyExpr="mappingName"
        formTitle={pageProps.formTitle}
        formColCount={2}
        formColSpan={2}
        allowAdding={true}
        allowUpdating={true}
        allowDeleting={true}
        columnConfigs={pageProps.columnConfigs}
        selectionMode="single"
        onSelectionChanged={onSelectionChanged}
        additionalButtons={additionalButtons}
        editorItemOptions={editorItemOptions}
        dataToBeFilteredForForm={dataToBeFilteredForForm}
        beforeSave={beforeSave}
      ></InactivatableCrudDataGrid>
      <Popup
        visible={popupVisible}
        onHiding={hidePopup}
        dragEnabled={false}
        hideOnOutsideClick={true}
        showCloseButton={true}
      >
        {getPopupContent()}
      </Popup>
    </>
  );
};
